import type { NavigationQuery } from "@azuro-org/toolkit"

interface LeagueDetail {
  id: string
  slug: string
  name: string
  turnover: string
}

interface SportDetail {
  id: string
  slug: string
  name: string
  sportId: string
  sporthub: {
    id: string
    slug: string
  }
  leagues: Map<number, LeagueDetail>
}

interface SxSportResponse {
  status: string
  data: Array<{
    sportId: number
    label: string
  }>
}

interface SxLeagueActiveResponse {
  status: string
  data: Array<{
    leagueId: number
    label: string
    sportId: number
  }>
}

interface SxNavigationResponse {
  sports: NavigationQuery["sports"]
}

// Sports to exclude from navigation
const excludedSports = [
  "custom",
  "nfts",
  "entertainment",
  "olympics",
  "economics",
  "medicinal",
  "daily parlays",
  "daily-parlays", // slug version
  "inplaylive", // exact match
  "inplaylive", // exact match
  "novelty-markets",
]

/**
 * Map sport names according to requirements
 */
const mapSportName = (originalName: string): string => {
  const sportNameMapping: Record<string, string> = {
    Soccer: "Football",
    Football: "American Football",
    "Mixed Martial Arts": "MMA",
  }

  return sportNameMapping[originalName] || originalName
}

/**
 * Map sport slugs according to requirements
 */
const mapSportSlug = (originalName: string): string => {
  const sportSlugMapping: Record<string, string> = {
    Soccer: "football",
    Football: "american-football",
    "Mixed Martial Arts": "mma",
  }

  const mappedName = sportSlugMapping[originalName] || originalName
  return mappedName.toLowerCase().replace(/\s+/g, "-")
}

/**
 * Transforms SX.bet sports and leagues data into the format expected by the Navigation component
 */
export const transformSxNavigationData = (
  sports: SxSportResponse["data"],
  leagues: SxLeagueActiveResponse["data"],
): SxNavigationResponse => {
  const sportDetails = new Map<number, SportDetail>()

  // Initialize all sports from the API response
  sports.forEach(sport => {
    sportDetails.set(sport.sportId, {
      id: sport.sportId.toString(),
      slug: mapSportSlug(sport.label),
      name: mapSportName(sport.label),
      sportId: sport.sportId.toString(),
      sporthub: {
        id: "sports",
        slug: "sports",
      },
      leagues: new Map(),
    })
  })

  // Process leagues without counting
  for (const league of leagues) {
    const { sportId, leagueId, label: leagueName } = league

    // Skip if sport doesn't exist or is excluded
    const sport = sports.find(s => s.sportId === sportId)
    if (!sport) {
      continue
    }

    const sportSlug = mapSportSlug(sport.label)
    const sportName = mapSportName(sport.label).toLowerCase()
    if (
      excludedSports.includes(sportSlug) ||
      excludedSports.includes(sportName)
    ) {
      continue
    }

    const sportData = sportDetails.get(sportId)
    if (!sportData) {
      continue
    }

    // Initialize league if not exists
    if (!sportData.leagues.has(leagueId)) {
      sportData.leagues.set(leagueId, {
        id: leagueId.toString(),
        slug: leagueName.toLowerCase().replace(/\s+/g, "-"),
        name: leagueName,
        turnover: "0",
      })
    }
  }

  // Convert to final format and output
  const transformedSports = Array.from(sportDetails.entries())
    .map(([sportId, details]) => {
      const leagues = Array.from(details.leagues.values())

      return {
        __typename: "Sport" as const,
        id: details.id,
        slug: details.slug,
        name: details.name,
        sportId: details.sportId,
        sporthub: details.sporthub,
        activeGamesCount: 0,
        activeLiveGamesCount: 0,
        activePrematchGamesCount: 0,
        countries: [
          {
            __typename: "Country" as const,
            id: `${details.id}_international`,
            slug: "international",
            name: "International",
            turnover: "0",
            activeGamesCount: 0,
            activeLiveGamesCount: 0,
            activePrematchGamesCount: 0,
            leagues: leagues.map(league => ({
              __typename: "League" as const,
              id: league.id,
              slug: league.slug,
              name: league.name,
              turnover: league.turnover,
              activeGamesCount: 0,
              activeLiveGamesCount: 0,
              activePrematchGamesCount: 0,
            })),
          },
        ],
      }
    })
    .filter(sport => {
      // Filter out excluded sports
      const sportSlug = sport.slug
      const sportName = sport.name.toLowerCase()
      return !(
        excludedSports.includes(sportSlug) || excludedSports.includes(sportName)
      )
    })

  return {
    sports: transformedSports,
  }
}
